import React, {cloneElement, CSSProperties, FocusEvent, Fragment, isValidElement, KeyboardEvent, ReactElement, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'

import {ensureFeedbackStyles, tooltipAnimation, tooltipStyle} from './styles.js'

ensureFeedbackStyles()

export type ToolTipPlacement = 'top' | 'bottom' | 'left' | 'right'

interface ToolTipProps {
    text?: ReactNode
    children?: ReactNode
    /**
     * bottom (default): below the anchor, centred; top: above it, centred; right / left: below it, from the middle of
     * the anchor to the right / to the left
     */
    placement?: ToolTipPlacement
    /** Shows the tooltip regardless of hover and focus (feedback like 'Copied') */
    visible?: boolean
    /** Never shows the tooltip. A disabled button keeps its tooltip: Button and IconButton don't pass this */
    disabled?: boolean
    /** Style of the wrapper around children (the anchor) */
    style?: CSSProperties
    /** Style of the tooltip itself */
    tooltipStyle?: CSSProperties
}

let nextId = 0

// Whether the focus came from the keyboard: a tooltip doesn't show for a mouse click that focuses a button
function isFocusVisible(element: Element) {
    try {
        return element.matches(':focus-visible')
    } catch {
        return true
    }
}

/**
 * A short text that shows near its children after hovering them for a moment, or right away when they get the
 * keyboard focus. Escape hides it. It is rendered in a portal on document.body with position fixed, so overflow
 * hidden containers and modals don't clip it, and follows its anchor on scroll and resize. The anchor (a single
 * element child) gets aria-describedby while the tooltip shows, unless its aria-label is the tooltip text already.
 */
export default function ToolTip({text, children, placement = 'bottom', visible, disabled, style, tooltipStyle: customStyle}: ToolTipProps) {
    const [hover, setHover] = useState(false)
    const [id] = useState(() => `tooltip-${++nextId}`)
    const anchorRef = useRef<HTMLDivElement>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)
    const timer = useRef<ReturnType<typeof setTimeout>>(null)

    useEffect(() => () => clearTimeout(timer.current), [])

    const shown = !!text && !disabled && (visible || hover)

    // Placed on the element directly, so scrolling doesn't re-render
    const place = useCallback(() => {
        const anchor = anchorRef.current
        const el = tooltipRef.current

        if (!anchor || !el) {
            return
        }

        const rect = anchor.getBoundingClientRect()
        const width = el.offsetWidth
        const middle = rect.left + rect.width / 2

        let left = placement === 'right' ? middle : placement === 'left' ? middle - width : middle - width / 2
        left = Math.max(0, Math.min(left, document.documentElement.clientWidth - width))

        el.style.left = `${left}px`
        el.style.top = `${placement === 'top' ? rect.top - tooltipStyle.gap - el.offsetHeight : rect.bottom + tooltipStyle.gap}px`
    }, [placement])

    useLayoutEffect(() => {
        if (!shown) {
            return
        }

        place()

        // Capture, so scrolling any container moves the tooltip along with its anchor
        window.addEventListener('scroll', place, true)
        window.addEventListener('resize', place)

        return () => {
            window.removeEventListener('scroll', place, true)
            window.removeEventListener('resize', place)
        }
    }, [shown, place, text])

    const showAfter = (delay: number, show: boolean) => {
        clearTimeout(timer.current)
        timer.current = setTimeout(() => setHover(show), delay)
    }

    const onFocus = (event: FocusEvent<HTMLDivElement>) => {
        if (isFocusVisible(event.target)) {
            clearTimeout(timer.current)
            setHover(true)
        }
    }

    const onBlur = () => {
        clearTimeout(timer.current)
        setHover(false)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && hover) {
            clearTimeout(timer.current)
            setHover(false)
        }
    }

    const tooltip: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        height: tooltipStyle.height,
        whiteSpace: 'nowrap',
        padding: `0px ${tooltipStyle.paddingX}px`,
        opacity: tooltipStyle.opacity,
        fontSize: tooltipStyle.fontSize,
        background: tooltipStyle.background,
        color: tooltipStyle.text,
        borderRadius: tooltipStyle.radius,
        zIndex: tooltipStyle.zIndex,
        // It floats over other content, the mouse goes through it
        pointerEvents: 'none',
        animation: `${tooltipAnimation} ${tooltipStyle.fadeIn}ms`,
        ...customStyle
    }

    const describe = shown && isValidElement(children) && children.type !== Fragment && (children as ReactElement).props['aria-label'] !== text
    const anchor = describe ? cloneElement(children as ReactElement, {'aria-describedby': id}) : children

    return (
        <div
            ref={anchorRef}
            style={{position: 'relative', ...style}}
            onMouseEnter={() => showAfter(tooltipStyle.showDelay, true)}
            onMouseLeave={() => showAfter(tooltipStyle.hideDelay, false)}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
        >
            {anchor}

            {shown && typeof document !== 'undefined' && ReactDOM.createPortal(
                <div ref={tooltipRef} id={id} role='tooltip' style={tooltip}>{text}</div>,
                document.body
            )}
        </div>
    )
}
